import type { Maybe, Message, MixedSchema, Schema } from 'yup';
import { mixed } from 'yup';

const defaultMessage: Message<Record<string, unknown>> = params => {
  const value = String(params.value ?? params.originalValue);
  const path = String(params.path);

  return `value (${value}) for ${path} did not match any schema`;
};

// Two
export function union<
  T1,
  T2,
  TCast1 extends Maybe<T1>,
  TCast2 extends Maybe<T2>,
  C1,
  C2,
  O1 extends T1,
  O2 extends T2,
>(
  schemas: [Schema<TCast1, C1, O1>, Schema<TCast2, C2, O2>],
  message?: Message<any>,
): MixedSchema<TCast1 | TCast2, C1 | C2, O1 | O2>;

// Three
export function union<
  T1,
  T2,
  T3,
  TCast1 extends Maybe<T1>,
  TCast2 extends Maybe<T2>,
  TCast3 extends Maybe<T3>,
  C1,
  C2,
  C3,
  O1 extends T1,
  O2 extends T2,
  O3 extends T3,
>(
  schemas: [
    Schema<TCast1, C1, O1>,
    Schema<TCast2, C2, O2>,
    Schema<TCast3, C3, O3>,
  ],
  message?: Message<any>,
): MixedSchema<TCast1 | TCast2 | TCast3, C1 | C2 | C3, O1 | O2 | O3>;

/**
 * Union support for Yup schemas. Unlike Zod, Yup has no schema concept that
 * maps to a union type in TypeScript 😩. This helper allows you to combine
 * schemas in a way that matches any one of the provided schemas and produces a
 * union type with Yup's `InferType` helper.
 *
 * NOTE: unlike Zod, Yup applies some default transforms to cast values for
 * particular schemas (like `number()`). This helper tries to preserve that
 * behavior, so it will apply the transform from the first matched schema in the
 * union.
 *
 * @example
 * ```ts
 * const schema = y.object({
 *   foo: union(
 *     [y.number().required(), y.string().required()],
 *     '"foo" must be a number or string'
 *   )
 * });
 *
 * type T = y.InferType<typeof schema>;
 * // { foo: number | string }
 *
 * schema.validateSync({ foo: 1 });
 * // 1
 *
 * schema.validateSync({ foo: '1' });
 * // 1
 *
 * schema.validateSync({ foo: 'bar' });
 * // 'bar'
 *
 * schema.validateSync({});
 * // Uncaught ValidationError: "foo" must be a number or string
 * ```
 *
 * @param schemas schemas to union, currently only 2 or 3 are supported
 * @param message optional custom message to use when none of the schemas match
 *
 * @remarks this was adapted from a comment on a GitHub issue, thanks @AverageHelper!
 * @see {@link https://github.com/jquense/yup/issues/593#issuecomment-1011536658}
 *
 * @returns Yup `Schema` that matches any of the provided schemas
 */
export function union<TCast extends Maybe<unknown>, C, O>(
  schemas: Array<Schema<TCast, C, O>>,
  message?: Message<any>,
): MixedSchema<TCast, C, O> {
  return mixed()
    .test({
      name: 'union',
      message: message ?? defaultMessage,
      test: value => schemas.some(s => s.isValidSync(value)),
    })
    .transform(value => {
      const schema = schemas.find(s => s.isValidSync(value));
      return schema?.cast(value);
    }) as MixedSchema<TCast, C, O>;
}
